import React, { useEffect, useState } from 'react'
import { X, Save } from 'lucide-react'
import { clientService, type Client } from '../../services/clientService'
import toast from 'react-hot-toast'

export interface ClientEditModalProps {
  isOpen: boolean
  onClose: () => void
  client: Client | null
  onSaved?: (updated: Client) => void
}

/**
 * Modal reutilizável para edição de dados do cliente.
 * Permite alterar name, email, document e skip_bot_menu. Telefone apenas leitura.
 */
export const ClientEditModal: React.FC<ClientEditModalProps> = ({
  isOpen,
  onClose,
  client,
  onSaved
}) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [document, setDocument] = useState('')
  const [skipBotMenu, setSkipBotMenu] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (client) {
      setName(client.name ?? '')
      setEmail(client.email ?? '')
      setDocument(client.document ?? '')
      setSkipBotMenu(client.skip_bot_menu ?? false)
    }
  }, [client])

  const handleSave = async () => {
    if (!client) return
    try {
      setIsSaving(true)
      const res = await clientService.updateClient(client.id, {
        name: name || undefined,
        email: email || undefined,
        document: document || undefined,
        skip_bot_menu: skipBotMenu
      })
      if (res.success && res.data) {
        onSaved?.(res.data)
        onClose()
        toast.success('Cliente atualizado')
      } else {
        toast.error(res.message || 'Erro ao atualizar cliente')
      }
    } catch {
      toast.error('Erro ao atualizar cliente')
    } finally {
      setIsSaving(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Editar cliente</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {client && (
            <>
              <div>
                <label htmlFor="client-edit-phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <input
                  id="client-edit-phone"
                  type="text"
                  value={client.phone_number}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600"
                />
              </div>
              <div>
                <label htmlFor="client-edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  id="client-edit-name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Nome do cliente"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="client-edit-email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                <input
                  id="client-edit-email"
                  type="text"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="E-mail"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="client-edit-document" className="block text-sm font-medium text-gray-700 mb-1">
                  Documento
                </label>
                <input
                  id="client-edit-document"
                  type="text"
                  value={document}
                  onChange={e => setDocument(e.target.value)}
                  placeholder="CPF/CNPJ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skipBotMenu}
                    onChange={e => setSkipBotMenu(e.target.checked)}
                    className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Pular menu do bot</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Quando ativo, o cliente não passa pelo menu do bot ao iniciar conversa.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            {isSaving ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Salvar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
